/* AuthProvider.jsx */
import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { isTokenExpired, isTokenExpiringSoon } from '../utils/tokenUtils';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

/* ------------------------------------------------------------ */
/* 1️⃣  Public routes – we only care about *pages*, not API calls. */
/* ------------------------------------------------------------ */
const publicPaths = [
  '/login',
  '/register',
  '/forgottenpassword',
  '/resetpassword',
  '/verify-email',
  '/welcome',
  '/help',
  '/',
];

/* ------------------------------------------------------------ */
/* 2️⃣  AuthProvider – the component that will wrap your app.   */
/* ------------------------------------------------------------ */
export const AuthProvider = ({ children }) => {
  /* ---------- router helpers --------------------------------- */
  const navigate = useNavigate();
  const location = useLocation();

  /* ---------- state & refs ----------------------------------- */
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const isLoggingOut = useRef(false);        // guard against double‑logout
  const tokenRef = useRef(token);            // always points to the latest token

  /* keep the ref in sync */
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  /* ---------- ENV ------------------------------------------------- */
  const API_URL = process.env.REACT_APP_API_URL;

  /* ------------------------------------------------------------------ */
  /* 3️⃣  LOGIN – stores tokens and clears any “logging‑out” flag.   */
  /* ------------------------------------------------------------------ */
  const login = (newToken, newRefreshToken) => {
    isLoggingOut.current = false; // reset if we were in the middle of a logout
    setToken(newToken);
    localStorage.setItem('token', newToken);
    if (newRefreshToken) {
      localStorage.setItem('refreshToken', newRefreshToken);
    }
  };

  /* ------------------------------------------------------------------ */
  /* 4️⃣  LOGOUT – clears everything and redirects to /login.        */
  /* ------------------------------------------------------------------ */
  const logout = useCallback(
    (reason = '') => {
      if (isLoggingOut.current) return; // already logging out
      isLoggingOut.current = true;

      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      setToken(null);

      navigate(`/login${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`, {
        replace: true,
      });

      /* allow a new action after 1 s */
      setTimeout(() => {
        isLoggingOut.current = false;
      }, 1000);
    },
    [navigate]
  );

  /* ------------------------------------------------------------------ */
  /* 5️⃣  INITIALIZE – read the token on mount.                       */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const storedToken = localStorage.getItem('token');

    /* If we already have a token, make sure it’s still valid. */
    if (storedToken) {
      if (isTokenExpired(storedToken)) {
        // Token is old → wipe everything and go to login
        logout('session-expired');
        return;
      }
      setToken(storedToken);          // token is good – keep it in state
    }

    setInitialized(true);
  }, [logout]);   // ← only run once on mount

  /* ------------------------------------------------------------------ */
  /* 6️⃣  Route guard – log out if we’re on a protected page but the
          token is missing or expired.                                 */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!initialized) return;

    if (!publicPaths.includes(location.pathname)) {
      const storedToken = localStorage.getItem('token');
      if (!storedToken || isTokenExpired(storedToken)) {
        logout();
      } else {
        setToken(storedToken);
      }
    }
  }, [location.pathname, initialized, logout]);

  /* ------------------------------------------------------------------ */
  /* 7️⃣  Token‑refresh helper – single promise, avoids race.          */
  /* ------------------------------------------------------------------ */
  const refreshingPromiseRef = useRef(null);

  const refreshToken = useCallback(async () => {
    if (isLoggingOut.current) return null;
    if (refreshingPromiseRef.current)
      return refreshingPromiseRef.current; // already in flight

    refreshingPromiseRef.current = (async () => {
      setLoading(true);
      try {
        const storedRefreshToken = localStorage.getItem('refreshToken');
        if (!storedRefreshToken) {
          console.warn('No refresh token – forcing logout');
          logout('session-expired');
          return null;
        }

        console.log("getting refresh token");

        const plainAxios = axios.create();

        const { data } = await plainAxios.post(`${API_URL}/auth/refresh`, {
          refreshToken: storedRefreshToken,
        });

        const { token: newAccessToken, refreshToken: newRefreshToken } = data;

        localStorage.setItem('token', newAccessToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        setToken(newAccessToken); // trigger re‑render

        return newAccessToken;
      } catch (err) {
        console.error('Token refresh failed', err);
        logout();
        return null;
      } finally {
        setLoading(false);
        refreshingPromiseRef.current = null; // reset for next call
      }
    })();

    return refreshingPromiseRef.current;
  }, [API_URL, logout]);

  /* ------------------------------------------------------------------ */
  /* 8️⃣  Axios interceptors – register once on mount.                */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    /* ---------- Request interceptor --------------------------------- */
    const reqInterceptor = axios.interceptors.request.use(
      async (config) => {
        // Skip public pages or if we’re logging out
        if (publicPaths.includes(location.pathname) || isLoggingOut.current)
          return config;

        const currentToken = localStorage.getItem('token');

        if (!currentToken) return config; // nothing to attach

        /* 8.1  Token is about to expire → refresh it first */
        if (isTokenExpiringSoon(currentToken)) {
          const newToken = await refreshToken();
          if (!newToken) {
            // Refresh failed – reject the request so our response interceptor
            // can log us out on a 401.
            return Promise.reject(new Error('Unable to refresh token'));
          }
          config.headers.Authorization = `Bearer ${newToken}`;
        } else {
          /* 8.2  Token is still good → attach it */
          config.headers.Authorization = `Bearer ${currentToken}`;
        }

        return config;
      },
      (err) => Promise.reject(err)
    );

    /* ---------- Response interceptor --------------------------------- */
    const resInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401) {
          logout();
        }
        return Promise.reject(error);
      }
    );

    /* Clean up on unmount / re‑creation */
    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
    };
  }, [location.pathname, refreshToken, logout]);

  /* ------------------------------------------------------------------ */
  /* 9️⃣  Background refresh – fire every X seconds if token is near
          expiry.                                                    */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!initialized) return;

    const interval = setInterval(() => {
      const token = localStorage.getItem('token');

      /* Only act when we have a *valid* token that is about to expire. */
      if (token && !isTokenExpired(token) && isTokenExpiringSoon(token)) {
        console.log('[AuthProvider] Background refresh triggered');
        refreshToken();
      }
    }, 45 * 1000);   // every 45 s

    return () => clearInterval(interval);
  }, [initialized, refreshToken]);

  /* ------------------------------------------------------------------ */
  /* 10️⃣  Provide the context value.                                  */
  /* ------------------------------------------------------------------ */
  return (
    <AuthContext.Provider
      value={{
        token,
        login,
        logout,
        loading,
        setLoading,
        refreshToken,
        initialized,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
